import { AuthenticationFacade } from '../auth';
import { Injectable, WritableSignal, computed, inject, signal } from '@angular/core';
import { LogService } from '../../providers';
import { SavePinService } from '../../providers/credentials/save-pin.service';
import { UserData, UserServiceInterface } from './interface';
import { VaultFacade } from '../vault';
import { filter, switchMap, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class UserService implements UserServiceInterface {
  private readonly authenticationService = inject(AuthenticationFacade);
  private readonly savePinService = inject(SavePinService);
  private readonly vaultService = inject(VaultFacade);

  private readonly userData: WritableSignal<UserData | null> = signal(null);
  public hasSecondFactor = computed(() => this.userData()?.hasSecondFactor);
  public userSecret = computed(() => this.userData()?.userSecret);
  public username = computed(() => this.userData()?.username);

  constructor() {
    this.authenticationService.authenticationChange$
      .pipe(
        takeUntilDestroyed(),
        tap((isAuthenticated) => {
          if (!isAuthenticated) {
            this.userData.set(null);
          }
        }),
        filter((isAuthenticated) => isAuthenticated),
        switchMap(() => this.vaultService.vaultReady),
        filter((isReady) => isReady),
        tap(async () => {
          try {
            const userData = await this.vaultService.getUserData();
            this.userData.set(userData);
          } catch (error) {
            LogService.error(`${JSON.stringify(error)}`);
            this.userData.set(null);
          }
        }),
      )
      .subscribe();
  }

  public async getSecondFactor(): Promise<string | null> {
    const username = this.username();
    if (username) {
      return this.savePinService.getPin();
    }
    return Promise.resolve(null);
  }

  public resetSecondFactor(): Promise<void> {
    const username = this.username();
    if (username) {
      return this.savePinService.resetPin();
    }
    return Promise.resolve();
  }

  public setSecondFactor(secondFactor: string): Promise<void> {
    if (secondFactor === null) {
      throw new Error('Second factor cannot be null');
    }
    const username = this.username();
    if (username) {
      return this.savePinService.setPin(username, secondFactor);
    }
    return Promise.resolve();
  }
}
